import type { LucideIcon } from "lucide-react";
import {
  Cloud,
  Focus,
  Layers,
  Map,
  MapPin,
  MoonStar,
  Sparkles,
  Stars,
  SunMoon,
} from "lucide-react";

/**
 * Generic layer registry — each layer registers once.
 * Adding albums/studios/heatmap later = one entry here + a show/hide hook
 * in the canvas applicator. No per-layer if/else scattered in the page.
 */

export type GlobeLayerId =
  | "dayNight"
  | "stars"
  | "clouds"
  | "borders"
  | "artists"
  | "playlists"
  | "cleanStage"
  | "focusedOnly";

export type GlobeLayerDef = {
  id: GlobeLayerId;
  icon: LucideIcon;
  label: string;
  defaultOn: boolean;
  /** Mode toggles sit below the visual layers in the strip. */
  group: "surface" | "data" | "mode";
  /** Hide from strip until parent mode is on. */
  requires?: GlobeLayerId;
};

export const GLOBE_LAYERS: readonly GlobeLayerDef[] = [
  {
    id: "dayNight",
    icon: SunMoon,
    label: "Day / night cycle",
    defaultOn: true,
    group: "surface",
  },
  {
    id: "stars",
    icon: Stars,
    label: "Star background",
    defaultOn: true,
    group: "surface",
  },
  {
    id: "clouds",
    icon: Cloud,
    label: "Clouds",
    defaultOn: true,
    group: "surface",
  },
  {
    id: "borders",
    icon: Map,
    label: "Borders + labels",
    defaultOn: true,
    group: "surface",
  },
  {
    id: "artists",
    icon: MapPin,
    label: "Artists",
    defaultOn: true,
    group: "data",
  },
  {
    id: "playlists",
    icon: Sparkles,
    label: "Playlists",
    defaultOn: true,
    group: "data",
  },
  {
    id: "cleanStage",
    icon: Focus,
    label: "Clean stage",
    defaultOn: false,
    group: "mode",
  },
  {
    id: "focusedOnly",
    icon: MoonStar,
    label: "Focused pin only",
    defaultOn: false,
    group: "mode",
    requires: "cleanStage",
  },
] as const;

export type GlobeLayerState = Record<GlobeLayerId, boolean>;

export function defaultLayerState(): GlobeLayerState {
  const state = {} as GlobeLayerState;
  for (const layer of GLOBE_LAYERS) {
    state[layer.id] = layer.defaultOn;
  }
  return state;
}

/** Layers visible in the strip given current state (respects `requires`). */
export function visibleLayerDefs(state: GlobeLayerState): GlobeLayerDef[] {
  return GLOBE_LAYERS.filter((l) => !l.requires || state[l.requires]);
}

export { Layers as LayersIcon };
